import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  currentUserSelector,
  deleteAccount,
  logout,
  updateUserFields,
} from '../redux/slices/userSlice';

/**
 * Custom hook for managing the settings modal state and functionality.
 * @returns - the required properties and functions for the settings modal.
 */
const useSettingsModal = () => {
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const currentUser = useAppSelector(currentUserSelector);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [username, setUsername] = useState(currentUser?.username);
  const [password, setPassword] = useState('');
  const [profilePicture, setProfilePicture] = useState(currentUser?.profilePicture);
  const [usernameEdit, setUsernameEdit] = useState(false);
  const [passwordEdit, setPasswordEdit] = useState(false);
  const [profilePictureEdit, setProfilePictureEdit] = useState(false);
  const [error, setError] = useState({
    username: '',
    password: '',
    profilePicture: '',
    deleteAccount: '',
  });

  /**
   * Function to reset the error state.
   */
  const resetError = () => {
    setError({ username: '', password: '', profilePicture: '', deleteAccount: '' });
  };

  /**
   * Function to handle the close event.
   * Resets the state of the modal.
   */
  const handleClose = () => {
    setOpen(false);
    setUsername(currentUser?.username);
    setPassword('');
    setProfilePicture(currentUser?.profilePicture);
    setUsernameEdit(false);
    setPasswordEdit(false);
    setProfilePictureEdit(false);
    resetError();
  };

  /**
   * Function to handle the username change event.
   * @param e - the event object.
   */
  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    resetError();
    setUsername(e.target.value);
  };

  /**
   * Function to handle the password change event.
   * @param e - the event object.
   */
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    resetError();
    setPassword(e.target.value);
  };

  /**
   * Function to handle the profile selection event.
   * @param picture - the selected profile picture URL.
   */
  const handleProfileSelect = (pictureUrl: string) => {
    resetError();
    setProfilePicture(pictureUrl);
  };

  /**
   * Function to handle the delete account event.
   */
  const handleDeleteAccount = () => {
    resetError();
    const uid = currentUser?._id;
    if (!uid) {
      throw new Error('Invalid UID');
    }

    const res = dispatch(deleteAccount(uid));
    res.unwrap().catch(() => {
      setError({
        username: error.username,
        password: error.password,
        profilePicture: error.profilePicture,
        deleteAccount: 'Unable to delete account. Please try again later.',
      });
    });

    dispatch(logout());
  };

  /**
   * Function to handle the submit event.
   */
  const handleSubmit = () => {
    setError({ username: '', password: '', profilePicture: '', deleteAccount: '' });
    const uid = currentUser?._id;
    if (!uid) {
      throw new Error('Invalid UID');
    }

    // Update username if changed
    if (username && username !== currentUser?.username) {
      const res = dispatch(updateUserFields({ uid, field: 'username', updatedValue: username }));
      res.unwrap().catch(() => {
        setUsername(currentUser?.username);
        setError({
          username: 'Unable to modify username. Please try again later.',
          password: error.password,
          profilePicture: error.profilePicture,
          deleteAccount: error.deleteAccount,
        }); // TODO: find a way to tell if the username is already taken
      });
      navigate(`/profile/${username}`);
    }

    // Update password if changed
    if (password) {
      const res = dispatch(updateUserFields({ uid, field: 'password', updatedValue: password }));
      res.unwrap().catch(() => {
        setPassword('');
        setError({
          username: error.username,
          password: 'Unable to modify password. Please try again later.',
          profilePicture: error.profilePicture,
          deleteAccount: error.deleteAccount,
        });
      });
    }

    // Update profile picture if changed
    if (profilePicture && profilePicture !== currentUser?.profilePicture) {
      const res = dispatch(
        updateUserFields({ uid, field: 'profilePicture', updatedValue: profilePicture }),
      );
      res.unwrap().catch(() => {
        setProfilePicture(currentUser?.profilePicture);
        setError({
          username: error.username,
          password: error.password,
          profilePicture: 'Unable to modify profile picture. Please try again later.',
          deleteAccount: error.deleteAccount,
        });
      });
    }
  };

  return {
    open,
    handleOpen,
    handleClose,
    usernameEdit,
    setUsernameEdit,
    username,
    handleUsernameChange,
    passwordEdit,
    setPasswordEdit,
    password,
    handlePasswordChange,
    profilePictureEdit,
    setProfilePictureEdit,
    profilePicture,
    handleProfileSelect,
    handleSubmit,
    handleDeleteAccount,
    error,
    resetError,
  };
};

export default useSettingsModal;
